import { useQuery } from '@tanstack/react-query';

import useActivityView from '@/data/hook/useActivityView';
import { updateDndOrdenationActivityProgress } from '@/data/services/activityElement/dndOrdenationActivityProgressServices';
import {
  dndOrdenationActivitiesProgressesQueryKeys,
  dndOrdenationActivitiesQueryKeys,
} from '@/data/services/querykeys';
import { ActivityTypeEnum } from '@/models/_deprecated/ActivityElement';
import { DNDOrdenationCorrectAnswer } from '@/models/ActivityProgress';
import LoadingView from '@/pages/courses/LoadingView';
import { getErrorMessage } from '@/utils/getErrorMessage';
import alert from '@/utils/shared/alert';
import { ElementPreviewFactoryProps } from '../ElementPreviewFactory';
import DNDOrdenationColumnList from './DNDOrdenationColumnList/DNDOrdenationColumnList';
import DNDOrdenationColumnProgressList from './DNDOrdenationColumnProgressList/DNDOrdenationColumnProgressList';

export interface DNDOrdenationElementProps extends ElementPreviewFactoryProps {
  answer?: DNDOrdenationCorrectAnswer | null;
}

export default function DNDOrdenationElement(props: DNDOrdenationElementProps) {
  const {
    questionId,
    progressId,
    isAdmin,
    activityMode,
    answer,
    attemptElement,
  } = props;
  const { invalidate, status } = useActivityView();
  const { queryKey: dndOrdenationProgressQueryKey, queryFn: progressQueryDn } =
    dndOrdenationActivitiesProgressesQueryKeys.get({
      dndOrdenationProgressId: progressId,
      questionId,
    });

  const {
    data: dndOrdenationActivity,
    isInitialLoading: isLoadingDndOrdenationActivity,
  } = useQuery({
    enabled: !!isAdmin,
    ...dndOrdenationActivitiesQueryKeys.get(questionId),
  });

  const {
    data: dndOrdenationActivityProgress,
    isInitialLoading: isLoadingDndOrdenationActivityProgress,
  } = useQuery({
    queryKey: dndOrdenationProgressQueryKey,
    queryFn: progressQueryDn,
    enabled:
      !isAdmin && !!questionId && !!progressId && status === 'inProgress',
  });

  const handleAnswer = async (hasChange: boolean) => {
    try {
      const data = await updateDndOrdenationActivityProgress(
        {
          questionId,
          dndOrdenationProgressId: progressId,
        },
        { isDone: hasChange },
      );

      if (data) {
        await invalidate(ActivityTypeEnum.DND_ORDENATION, data);
      }
    } catch (error) {
      alert.error(getErrorMessage(error));
    }
  };

  if (
    isLoadingDndOrdenationActivity ||
    isLoadingDndOrdenationActivityProgress
  ) {
    return <LoadingView />;
  }

  if (dndOrdenationActivity) {
    return (
      <DNDOrdenationColumnList dndOrdenationActivity={dndOrdenationActivity} />
    );
  }

  if (dndOrdenationActivityProgress) {
    return (
      <DNDOrdenationColumnProgressList
        dndOrdenationActivityProgress={dndOrdenationActivityProgress}
        attemptElement={attemptElement}
        activityMode={activityMode}
        setHasChange={handleAnswer}
        answerColumns={answer?.columns}
      />
    );
  }

  return null;
}
